/* FNDRY - PDP timeline spine
   Plain JS, no framework.

   Failure direction: the markup ships with every stage lit and the spine full,
   so a script that never runs leaves a finished timeline rather than a column
   of ghosts. This file dims the stages on init and then drives both from
   scroll. The catch puts everything back.

   Driven from scroll rather than an IntersectionObserver on purpose: the same
   section is previewed inside a scrolling container in the design mockups, and
   an observer needs a root it cannot always be given. Six rect reads a frame
   is cheap. */
(function () {
  'use strict';

  var ROOT = '[data-fndry-ptl]';

  function slice(list) {
    return Array.prototype.slice.call(list || []);
  }

  function reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function Timeline(root) {
    this.root = root;
    this.list = root.querySelector('[data-ptl-list]');
    this.steps = slice(root.querySelectorAll('[data-ptl-step]'));
    if (!this.list || !this.steps.length) return;

    if (root.getAttribute('data-ptl-animate') === 'false' || reduced()) {
      this.list.style.setProperty('--fill', '1');
      return;
    }

    var line = parseFloat(root.getAttribute('data-ptl-line'));
    this.line = isNaN(line) ? 0.62 : line / 100;

    /* Only now does the pre-animation state exist. */
    this.steps.forEach(function (step) {
      step.classList.remove('is-in');
      step.setAttribute('data-ptl-dim', '');
    });
    this.list.style.setProperty('--fill', '0');

    this.bind();
    this.update();
  }

  /* P15 2026-10-23: this used to run on every scroll event of the page, from
     top to footer, and wrote --fill before reading each stage's rect - a forced
     style recalc and layout per event, the heaviest scroll handler on the
     product pages. Now it runs at most once a frame, only while the section is
     within a screen of the viewport, reads everything before it writes, and
     writes nothing that has not changed. What it shows is unchanged. */
  Timeline.prototype.bind = function () {
    var self = this;
    this.frame = 0;
    this.fill = null;
    this.waiting = this.steps.slice();
    this.near = !('IntersectionObserver' in window);

    this.onScroll = function () {
      if (!self.near || self.frame) return;
      self.frame = window.requestAnimationFrame(function () {
        self.frame = 0;
        self.update();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    if (!this.near) {
      this.io = new IntersectionObserver(function (entries) {
        self.near = entries[entries.length - 1].isIntersecting;
        if (self.frame) {
          window.cancelAnimationFrame(self.frame);
          self.frame = 0;
        }
        /* One update on the way in and on the way out, so the spine rests
           empty below the section and full above it, as before. */
        self.update();
      }, { rootMargin: '100% 0px 100% 0px' });
      this.io.observe(this.root);
    }
  };

  Timeline.prototype.update = function () {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var box = this.list.getBoundingClientRect();
    var reading = vh * this.line;
    var p = Math.max(0, Math.min(1, (reading - box.top) / Math.max(box.height, 1)));

    /* Latch: a stage that has been read stays read. Draining the spine while
       the section is still on screen reads as a bug, not as feedback.
       All reads first, then the writes. */
    var limit = vh * 0.86;
    var due = [];
    this.waiting = this.waiting.filter(function (step) {
      if (step.getBoundingClientRect().top < limit) {
        due.push(step);
        return false;
      }
      return true;
    });

    var fill = String(Math.round(p * 10000) / 10000);
    if (fill !== this.fill) {
      this.fill = fill;
      this.list.style.setProperty('--fill', fill);
    }
    due.forEach(function (step) {
      step.classList.add('is-in');
      step.removeAttribute('data-ptl-dim');
    });
  };

  Timeline.prototype.destroy = function () {
    if (!this.onScroll) return;
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.io) this.io.disconnect();
    if (this.frame) window.cancelAnimationFrame(this.frame);
  };

  function reveal(root) {
    var list = root.querySelector('[data-ptl-list]');
    if (list) list.style.setProperty('--fill', '1');
    slice(root.querySelectorAll('[data-ptl-step]')).forEach(function (step) {
      step.classList.add('is-in');
      step.removeAttribute('data-ptl-dim');
    });
  }

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var found = slice(context.querySelectorAll(ROOT));
    if (context.matches && context.matches(ROOT)) found.unshift(context);

    found.forEach(function (root) {
      if (root.getAttribute('data-ptl-ready') === 'true') return;
      root.setAttribute('data-ptl-ready', 'true');
      try {
        root.fndryTimeline = new Timeline(root);
      } catch (error) {
        reveal(root);
        if (window.console && console.error) console.error('[fndry-pdp-timeline]', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    if (!target || !target.querySelector) return;
    var root = target.matches && target.matches(ROOT) ? target : target.querySelector(ROOT);
    if (!root) return;
    if (root.fndryTimeline && root.fndryTimeline.destroy) root.fndryTimeline.destroy();
    root.removeAttribute('data-ptl-ready');
  });

  /* Selecting a stage in the editor should show it, not leave it dimmed at
     whatever scroll position the editor happens to be at. */
  document.addEventListener('shopify:block:select', function (event) {
    var block = event.target;
    if (!block || !block.closest) return;
    var root = block.closest(ROOT);
    if (!root) return;
    block.classList.add('is-in');
    block.removeAttribute('data-ptl-dim');
  });
})();
